import createContext from 'bz-utilities/hooks/context-v2';
import { GetAllowedPlaceholderServicesForCase } from 'caseflow/api.dtos';
import { client } from 'connect/core/client';
import { action, computed, observable, runInAction } from 'mobx';
import { computedFn } from 'mobx-utils';
import cache from 'src/cache';
import type {
  DynamicLabelResponse,
  Group,
  Lookup,
  MedicalImagingProcedure,
  OnlineLocationDto,
  OnlinePractitionerDto,
  OnlineSection,
  OnlineServiceCategory,
  PackageResponse,
  ValidateBookingLinkRequest
} from 'tm3-core/api.dtos';
import {
  ConnectValidateAccessCodeRequest,
  ConnectValidateBusinessRouteRequest,
  OnlineClinicService,
  OnlineInitialRequest
} from 'tm3-core/api.dtos';

export enum SearchType {
  Initial,
  Appointment,
  Class
}

export enum SearchTypePayment {
  All,
  SelfPay,
  Insurance
}

export const [SearchStoreCtx, useSearchStore] = createContext<SearchStore>('SearchStore');

class SearchStore {
  @observable loading = true;
  @observable placeHolderLoading = true;

  @observable loaded = false;
  @observable services = observable.array<OnlineClinicService>([]);
  @observable imagingProcedures = observable.array<MedicalImagingProcedure>([]);
  @observable categories = observable.array<OnlineServiceCategory>([]);
  @observable sections = observable.array<OnlineSection>([]);
  @observable _packages = observable.array<PackageResponse>([]);
  @observable excludedPackages = observable.array<string>([]);

  @observable practitioners = observable.array<OnlinePractitionerDto>([]);
  @observable locations = observable.array<OnlineLocationDto>([]);
  @observable groups = observable.array<Group>([]);
  @observable lookups = observable.array<Lookup>([]);
  @observable dynamicLabels = observable.array<DynamicLabelResponse>([]);
  @observable _allowedPlaceholderServices = observable.array<OnlineClinicService>([]);
  @observable validPath = false;
  @observable pathError = 'Invalid Path';
  @observable mode = SearchType.Initial;
  @observable paymentMode = SearchTypePayment.All;
  @observable locationId: string | null = null;
  @observable isWaitingListFeatureOn: boolean = false;
  @observable isCaseflow: boolean = false;
  @observable isJourneysEnabled: boolean = false;
  @observable isCaseflowPluginEnabled: boolean = false;
  @observable accessCodePassed: boolean = false;
  @observable accessCodeLoading: boolean = false;
  @observable accessCodeFailed: boolean = false;
  @observable forceService?: OnlineClinicService = undefined; // Caseflow placeholder service selection
  @observable isMfaEnabled?: boolean;

  @observable baseService?: OnlineClinicService = undefined;
  @observable bookingLinkData?: ValidateBookingLinkRequest;

  loadingPromise!: Promise<void>;

  private readonly isActiveClass = computedFn((s: OnlineClinicService): boolean => {
    if (!s.isClassService || s.isCourse) {
      return false;
    }

    return this.categories.some((c) => this.isCategoryOnline(c, s));
  });

  private readonly isCategoryOnline = computedFn(
    (c: OnlineServiceCategory, s: OnlineClinicService): boolean => {
      const orderedServices = c.orderedServices ?? [];
      const isOnline = orderedServices.some(
        (serviceId) => serviceId === s.id && c.isOnline && !c.hideFromExplore
      );

      if (isOnline) {
        return true;
      }

      // [Tomasz] - This recursively checks child categories
      return this.categories.some((cc) => this.isChildCategoryOnline(cc, c, s));
    }
  );

  private readonly isChildCategoryOnline = computedFn(
    (cc: OnlineServiceCategory, c: OnlineServiceCategory, s: OnlineClinicService): boolean => {
      return cc.id === c.parentId && this.isCategoryOnline(cc, s);
    }
  );

  @computed
  get packages(): PackageResponse[] {
    return this._packages.filter((x) => !this.excludedPackages.includes(x.onlineGuid ?? ''));
  }

  @computed
  get hasClasses(): boolean {
    return this.services.some((s) => this.isActiveClass(s));
  }

  @computed
  get hasAppointments(): boolean {
    return this.services.some((s) => {
      if (s.isClassService || s.isCourse) {
        return false;
      }

      return this.categories.some((c) => {
        const orderedServices = c.orderedServices ?? [];
        return orderedServices.some(
          (serviceId) => serviceId === s.id && c.isOnline && !c.parentId && !c.hideFromExplore
        );
      });
    });
  }

  @computed
  get hasInsurancePaidServices(): boolean {
    return this.services.some((s) => {
      if (!s.insurancePaid) {
        return false;
      }

      return this.categories.some((c) => this.isCategoryOnline(c, s));
    });
  }

  @computed
  get servicesAvailable(): OnlineClinicService[] {
    const customerGroupId = cache.authStore?.groupId;

    return this.services.filter((service) => {
      if (!service.insurancePaid) {
        return true;
      }

      // If customer has insurance provider, only show compatible services
      if (customerGroupId) {
        return service.supportedGroupIds?.some((id) => String(id) === String(customerGroupId));
      }

      return true;
    });
  }

  @computed
  get allowedPlaceholderServices() {
    const { _allowedPlaceholderServices } = this;
    const { baseService } = this;
    return _allowedPlaceholderServices.map(
      (override) =>
        new OnlineClinicService({
          ...baseService,
          ...override
        })
    );
  }

  @action.bound
  async load(): Promise<void> {
    this.loading = true;

    try {
      const res = await client.get(new OnlineInitialRequest());
      const serviceId = localStorage.getItem('accessCodeServiceId');
      const accessCode = localStorage.getItem('accessCode');

      runInAction(() => {
        const {
          categories,
          services,
          practitioners,
          locations,
          groups,
          sections,
          packages,
          lookups,
          dynamicLabels,
          isWaitingListEnabled,
          isCaseflow,
          isJourneysEnabled,
          isCaseflowPluginEnabled,
          isMfaEnabled,
          imagingProcedures
        } = res!.result!;

        this.categories.replace(categories);
        this.services.replace(services);
        this.locations.replace(locations);
        this.practitioners.replace(practitioners);
        this.groups.replace(groups);
        this.sections.replace(sections);
        this._packages.replace(packages);
        this.lookups.replace(lookups);
        this.dynamicLabels.replace(dynamicLabels);
        this.isWaitingListFeatureOn = isWaitingListEnabled;
        this.isMfaEnabled = isMfaEnabled;
        this.imagingProcedures.replace(imagingProcedures ?? []);
        this.isCaseflow = isCaseflow;
        this.isJourneysEnabled = isJourneysEnabled;
        this.isCaseflowPluginEnabled = isCaseflowPluginEnabled;
        if (serviceId && accessCode) {
          this.validateAccessCode(serviceId, accessCode);
        }
        this.loaded = true;
        this.loading = false;
      });
    } catch (e) {
      console.error('[SearchStore.load] - An error has occurred:', e);
      runInAction(() => {
        this.loaded = true;
        this.loading = false;
      });
    }
  }

  @action.bound
  trackPackagePurchase(packageId?: string): void {
    if (!packageId) {
      return;
    }

    let pkg = this._packages.find((x) => x.onlineGuid === packageId);

    if (!pkg) {
      return;
    }

    if (pkg.newCustomersOnly || pkg.onePerCustomer) {
      this.excludedPackages.push(pkg.onlineGuid!);
    }
  }

  async validateUrl(url: string): Promise<void> {
    await this.loadingPromise;
    const routes = url.split('/').slice(2);

    if (routes.length === 1) {
      const service = this.services.find((s) => {
        return routes[0] === s.urlSlug;
      });

      if (service != null) {
        this.validPath = true;
        return;
      }
    }

    let currentCategory: OnlineServiceCategory | null = null;

    const foundRoute = routes.every((route, i) => {
      const cat = this.categories.find((c) => {
        return route === c.urlSlug;
      });

      if (currentCategory == null && cat != null && cat.parentId == null) {
        currentCategory = cat;
        return true;
      }

      if (currentCategory != null && cat != null) {
        const val = currentCategory.id === cat.parentId;

        currentCategory = cat;
        return val;
      }

      if (routes.length - 1 === i && currentCategory != null) {
        const matchingServices = this.services.filter((s) => {
          return route === s.urlSlug;
        });

        return (
          matchingServices != null &&
          currentCategory.orderedServices!.some((id) => matchingServices.some((m) => m.id === id))
        );
      }

      return false;
    });
    if (foundRoute) this.validPath = true;
    else await this.validateBusinessRoutes(url);
  }

  @action
  async validateBusinessRoutes(url: string): Promise<void> {
    this.loading = true;
    const { result } = await client.post(new ConnectValidateBusinessRouteRequest({ url }));
    if (result!.isMatch)
      runInAction(() => {
        this.categories.push(...result!.categories);
        this.services.push(...result!.services);
        this.validPath = true;
        this.loading = false;
      });
    else
      runInAction(() => {
        this.validPath = false;
        this.loading = false;
        this.pathError = result!.reason;
      });
  }

  @action.bound
  async validateAccessCode(serviceId: string, accessCode: string): Promise<void> {
    this.accessCodeLoading = true;
    const res = await client.post(new ConnectValidateAccessCodeRequest({ serviceId, accessCode }));
    runInAction(() => {
      if (res.result) {
        this.accessCodePassed = true;
        localStorage.setItem('accessCodeServiceId', serviceId);
        localStorage.setItem('accessCode', accessCode);
      } else this.accessCodeFailed = true;
      this.accessCodeLoading = false;
    });
  }

  @action.bound
  async loadAllowedPlaceholderServices(baseService: OnlineClinicService) {
    this.placeHolderLoading = true;
    this.baseService = baseService;
    const subWorkflowId = sessionStorage.getItem('subWorkflowId');
    const slug = window.location.pathname.split('/')[2];

    const res = await client.get(
      new GetAllowedPlaceholderServicesForCase({
        connectServiceId: baseService.id,
        subWorkflowId: subWorkflowId,
        productId: baseService.productId,
        categorySlug: slug
      })
    );
    runInAction(() => {
      this.placeHolderLoading = false;
      this._allowedPlaceholderServices.replace(res.results!);
      if (this._allowedPlaceholderServices.length === 1) {
        this.forceService = this.allowedPlaceholderServices[0];
      }
    });
  }
}

export default SearchStore;
